import { IoMdShare } from "react-icons/io";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../utils/hooks";
import { getRequest, postRequest } from "../apiRequests/requestApi";

// Components
import DisplayUsers from "./auxiliary/DisplayUsers";
import ShareModal from "./auxiliary/ShareModal";

// Styles
import "./styles/ActiveUsers.css";

const INITIAL_MODAL_STATE = {
  visible: false,
  onHide: null,
  size: "md",
};

export default function ActiveUsers({
  btnTxt = "share",
  btnFunc,
  gameId,
  qrValue,
  lobbyCode,
  gameName,
  currentPlayerId,
}) {
  const navigate = useNavigate();
  const { user } = useUser();
  const [shareModal, setShareModal] = useState(INITIAL_MODAL_STATE);
  const [activeUsers, setActiveUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Import socketManager dynamically to avoid circular dependencies
    let isMounted = true;
    let socketManagerModule = null;

    // Function to fetch active users through socket
    const fetchActiveUsersThroughSocket = () => {
      if (!isMounted) return;

      // Only proceed if we have socketManager
      if (!socketManagerModule) return;

      // Get socket reference
      const socket = socketManagerModule.socket;

      // If socket disconnected, try to reconnect with user data
      if (!socket.connected && user) {
        // console.log("Socket disconnected, attempting to reconnect");
        socketManagerModule.connect({
          userId: user._id,
          username: user.username,
        });
      }

      // Set up handler for active users
      const handleActiveUsers = (users) => {
        if (!isMounted) return;

        // console.log("Received active users through socket:", users);

        if (Array.isArray(users)) {
          // Filter out null/undefined entries and our own user
          const filteredUsers = users.filter(
            (user) => user && (user.userID || user.userId || user._id)
          );

          if (filteredUsers.length > 0) {
            setActiveUsers(filteredUsers);
            setIsLoading(false);
          } else {
            // If empty array, try API fallback
            fetchActiveUsersThroughAPI();
          }
        } else {
          // If invalid data, try API fallback
          fetchActiveUsersThroughAPI();
        }
      };

      // Remove any existing handlers to prevent duplicates
      socket.off("get_active", handleActiveUsers);

      // Add handler
      socket.on("get_active", handleActiveUsers);

      // Request active users
      // console.log("Requesting active users through socket");
      socket.emit("get_active_users");

      // Return cleanup function for this sub-effect
      return () => {
        socket.off("get_active", handleActiveUsers);
      };
    };

    // Function to fetch active users through API
    const fetchActiveUsersThroughAPI = async () => {
      if (!isMounted) return;

      try {
        setIsLoading(true);
        // console.log("Fetching active users through API");
        const response = await getRequest("/active-users");
        if (isMounted) {
          if (response && response.data && Array.isArray(response.data)) {
            setActiveUsers(response.data);
          } else {
            setActiveUsers([]);
          }
        }
      } catch (error) {
        // console.error("Error fetching active users from API:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // The main effect function
    const initializeActiveUsers = async () => {
      try {
        // First try to load socketManager
        socketManagerModule = await import("../../socket").then(
          (module) => module.default
        );

        // First attempt: try both methods
        fetchActiveUsersThroughAPI();
        const socketCleanup = fetchActiveUsersThroughSocket();

        // Set up intervals
        const apiInterval = setInterval(fetchActiveUsersThroughAPI, 30000);
        const socketInterval = setInterval(
          fetchActiveUsersThroughSocket,
          15000
        );

        // Return cleanup function
        return () => {
          clearInterval(apiInterval);
          clearInterval(socketInterval);
          if (typeof socketCleanup === "function") {
            socketCleanup();
          }
        };
      } catch (error) {
        // console.error("Error importing socketManager:", error);

        // Fall back to API-only approach
        fetchActiveUsersThroughAPI();
        const interval = setInterval(fetchActiveUsersThroughAPI, 15000);

        // Return cleanup function
        return () => clearInterval(interval);
      }
    };

    // Start the process and capture cleanup function
    const cleanup = initializeActiveUsers();

    // The main cleanup function
    return () => {
      isMounted = false;
      if (typeof cleanup === "function") {
        cleanup();
      }
    };
  }, [user, activeUsers.length]);

  const handleUserSelect = async (selectedUser) => {
    // Extract opponent ID from the user object - try all possible ID properties
    const opponentId =
      selectedUser.user_id ||
      selectedUser.userID ||
      selectedUser._id ||
      selectedUser.id;
    const opponentAvatar = selectedUser.avatar;
    const opponentName = selectedUser.username;
    // console.log("Selected opponent:", selectedUser, "Opponent ID:", opponentId);
    // console.log("Game data received:", {
    //   gameId,
    //   currentPlayerId,
    //   qrValue,
    //   lobbyCode,
    //   gameName,
    // });

    if (gameId && opponentId && currentPlayerId && lobbyCode) {
      // Construct the game URL with query parameters (no trailing slash)

      // Extract lobbyId correctly - if qrValue is an object with _id, use that, otherwise use qrValue directly
      const lobbyId =
        typeof qrValue === "object" && qrValue._id ? qrValue._id : qrValue;

      // Format game name properly (capitalize first letter)
      const formattedGameName = gameName
        ? gameName.charAt(0).toUpperCase() + gameName.slice(1)
        : "Chess";

      // Make the API call to select player before navigation
      try {
        const response = await postRequest({
          url: "/gamers/select",
          data: {
            playerId: opponentId,
            lobbyId: lobbyId,
          },
        });

        // console.log("Player selection API response:", response);

        // If API call is successful, proceed with navigation
        navigate(
          `/games/selected-user/${gameId}/${currentPlayerId}/${opponentId}/${opponentName}/${lobbyId}/${lobbyCode}/${gameName}?avatar=${encodeURIComponent(
            opponentAvatar
          )}`
        );

        // console.log("URL parameters:", {
        //   gameId,
        //   playerId: currentPlayerId,
        //   opponentId,
        //   gameName: formattedGameName,
        //   lobbyId,
        //   lobbyCode,
        // });
      } catch (error) {
        // console.error("Error selecting player:", error);
        // Still navigate even if API call fails to maintain user experience
        navigate(
          `/games/selected-user/${gameId}/${currentPlayerId}/${opponentId}/${opponentName}/${lobbyId}/${lobbyCode}/${gameName}?avatar=${encodeURIComponent(
            opponentAvatar
          )}`
        );
      }
    } else if (btnFunc) {
      // Include the opponent ID in the call to the provided button function
      btnFunc({ ...selectedUser, opponentId });
    } else {
      // console.error("Missing required parameters for game navigation");
    }
  };

  const openShareModal = () => {
    setShareModal({
      ...INITIAL_MODAL_STATE,
      visible: true,
      onHide: hideShareModal,
    });
  };

  const hideShareModal = () => {
    setShareModal(INITIAL_MODAL_STATE);
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#130828] px-4 py-5">
      {/* Header */}

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-white text-xl font-semibold">
          Active <span className="text-[#FF43B9]">Users</span>
        </h1>

        <button
          className="text-white"
          onClick={openShareModal}
          aria-label="Share"
        >
          <IoMdShare color="#FFF" size={24} />
        </button>
      </div>

      {/* Users List */}
      <div className="w-full">
        {isLoading ? (
          <div className="text-center text-white p-4">Loading users...</div>
        ) : activeUsers.length > 0 ? (
          <DisplayUsers
            users={activeUsers
              .filter(
                (u) =>
                  (u.userId || u.userID || u._id || u.id) !== user?._id &&
                  u.role !== "celebrity" &&
                  u.accountType !== "celebrity" &&
                  !u.isCelebrity
              )
              .map((user) => {
                const opponentId = user.user_id || user._id || user.id;
                return { ...user, opponentId };
              })}
            btnTxt={btnTxt}
            btnFunc={
              gameId && currentPlayerId && qrValue && lobbyCode && gameName
                ? handleUserSelect
                : btnFunc
            }
          />
        ) : (
          <div className="text-center text-white p-4">
            No active users found
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal
        modalProps={shareModal}
        gameName={gameName}
        lobbyCode={lobbyCode}
      />
    </div>
  );
}
